import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { AppDialog } from '@/components/ui/app-dialog.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
  caseRevisionsQueryOptions,
  poorlyDescribedFindingsQueryOptions,
  useApplyCaseRevision,
  useDismissCaseRevision,
  useRefineCases,
  useRestartRefined,
} from '@/lib/api/quality.js';
import type {
  CaseProblem,
  CaseRevision,
  CaseSpec,
  ReplayFinding,
  TestStep,
} from '@/lib/api/quality-types.js';
import { htmlToText } from '@/lib/html-to-text.js';
import { notify } from '@/lib/notify.js';

interface AffectedCase {
  caseId: string;
  title: string;
  problems: CaseProblem[];
  reason: string | null;
  revision: CaseRevision | null;
}

/**
 * Strip the HTML that TestRail stores in a case's text fields (title, precondition, step
 * content/expected, expected result) to plain text — for readable display and so an applied rewrite
 * writes clean text back rather than raw `<p>…</p>` markup.
 */
function toPlainSpec(spec: CaseSpec): CaseSpec {
  return {
    ...spec,
    title: spec.title != null ? htmlToText(spec.title) : spec.title,
    precondition:
      spec.precondition != null
        ? htmlToText(spec.precondition)
        : spec.precondition,
    expected_result:
      spec.expected_result != null
        ? htmlToText(spec.expected_result)
        : spec.expected_result,
    test_steps_text:
      spec.test_steps_text != null
        ? htmlToText(spec.test_steps_text)
        : spec.test_steps_text,
    test_steps: spec.test_steps?.map((step) => ({
      ...step,
      content: step.content != null ? htmlToText(step.content) : step.content,
      expected:
        step.expected != null ? htmlToText(step.expected) : step.expected,
    })),
  };
}

/** Read the poorly_described_case finding payload defensively (server data, cast not validated). */
function fromFinding(finding: ReplayFinding): AffectedCase | null {
  const payload = finding.payload ?? {};
  const caseId = payload.caseRef ?? payload.case_ref;
  if (typeof caseId !== 'string') return null;
  const title = typeof payload.title === 'string' ? payload.title : caseId;
  const problems = Array.isArray(payload.problems)
    ? (payload.problems as CaseProblem[])
    : [];
  const reason = typeof payload.reason === 'string' ? payload.reason : null;
  return { caseId, title, problems, reason, revision: null };
}

/**
 * The refine surface for a failed run (MEDA-352). Shows an explicit banner when the classification
 * judge flagged failures as poorly-written test cases (not app defects), lists each affected case
 * with a Refine action (per case + bulk), lets the user review each rewrite before applying, and
 * re-runs only the refined cases. Renders nothing when the run has no flagged cases.
 */
export function RefinePanel({
  runId,
  enabled,
}: {
  runId: string;
  enabled: boolean;
}): JSX.Element | null {
  const findingsQuery = useQuery(
    poorlyDescribedFindingsQueryOptions(runId, enabled),
  );
  const revisionsQuery = useQuery(caseRevisionsQueryOptions(runId, enabled));
  const refine = useRefineCases(runId);
  const restartRefined = useRestartRefined(runId);
  const navigate = useNavigate();
  const [reviewing, setReviewing] = useState<CaseRevision | null>(null);

  const cases = useMemo<AffectedCase[]>(() => {
    const revisions = revisionsQuery.data ?? [];
    const merged = new Map<string, AffectedCase>();
    for (const finding of findingsQuery.data ?? []) {
      const affected = fromFinding(finding);
      if (affected) merged.set(affected.caseId, affected);
    }
    // A revision may outlive its finding (e.g. a re-refine after dismiss) — surface it either way.
    for (const revision of revisions) {
      const existing = merged.get(revision.case_id);
      if (existing) existing.revision = revision;
      else
        merged.set(revision.case_id, {
          caseId: revision.case_id,
          title: revision.original_spec?.title ?? revision.case_id,
          problems: revision.problems ?? [],
          reason: null,
          revision,
        });
    }
    return [...merged.values()];
  }, [findingsQuery.data, revisionsQuery.data]);

  if (cases.length === 0) return null;

  const refinable = cases.filter(
    (c) =>
      !c.revision ||
      c.revision.status === 'dismissed' ||
      c.revision.status === 'failed',
  );
  const appliedCaseIds = cases
    .filter((c) => c.revision?.status === 'applied')
    .map((c) => c.caseId);

  const refineCaseIds = (ids: string[]): void => {
    refine.mutate(ids, {
      onError: (error) => notify.error(error.message),
    });
  };

  const rerunRefined = (): void => {
    restartRefined.mutate(appliedCaseIds, {
      onSuccess: (created) => {
        notify.success('Re-running the refined cases');
        // The scoped re-run mints a NEW run — jump to it so the user can watch it (matches the
        // full re-run action on the run detail page).
        void navigate({ to: '/quality/$id', params: { id: created.id } });
      },
      onError: (error) => notify.error(error.message),
    });
  };

  return (
    <Card className="border-warning-500/40">
      <CardHeader>
        <CardTitle className="text-base">
          Some tests failed due to poorly written test cases
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          These failures look like authoring problems (ambiguous steps, missing
          context, or unclear expected results), not application defects. Review
          and refine them below, then re-run only the refined cases.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            onClick={() => refineCaseIds(refinable.map((c) => c.caseId))}
            onClickCapture={(event) => {
              if (refinable.length === 0 || refine.isPending) {
                event.preventDefault();
                event.stopPropagation();
                notify.warning(
                  'No cases are ready to refine. Wait for the current refinement or choose an eligible case.',
                );
              }
            }}
          >
            {refine.isPending
              ? 'Refining…'
              : `Refine all (${refinable.length})`}
          </Button>
          {appliedCaseIds.length > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={rerunRefined}
              onClickCapture={(event) => {
                if (restartRefined.isPending) {
                  event.preventDefault();
                  event.stopPropagation();
                  notify.warning(
                    'Wait for the current action to finish, then try again.',
                  );
                }
              }}
            >
              {restartRefined.isPending
                ? 'Re-running…'
                : `Re-run refined cases (${appliedCaseIds.length})`}
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {cases.map((affected) => (
          <CaseRow
            key={affected.caseId}
            affected={affected}
            busy={refine.isPending}
            onRefine={() => refineCaseIds([affected.caseId])}
            onReview={() =>
              affected.revision && setReviewing(affected.revision)
            }
          />
        ))}
      </CardContent>
      {reviewing ? (
        <ReviewDialog
          runId={runId}
          revision={reviewing}
          onClose={() => setReviewing(null)}
        />
      ) : null}
    </Card>
  );
}

/** One affected-case row: title, problems, current status, and the appropriate action. */
function CaseRow({
  affected,
  busy,
  onRefine,
  onReview,
}: {
  affected: AffectedCase;
  busy: boolean;
  onRefine: () => void;
  onReview: () => void;
}): JSX.Element {
  const status = affected.revision?.status;
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border p-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{affected.title}</span>
          <Badge variant="warning">Poorly described</Badge>
        </div>
        {affected.problems.length > 0 ? (
          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
            {affected.problems.map((problem, index) => (
              <li key={index}>
                {problem.kind}
                {typeof problem.stepIndex === 'number'
                  ? ` (step ${problem.stepIndex + 1})`
                  : ''}
                {problem.note ? `: ${problem.note}` : ''}
              </li>
            ))}
          </ul>
        ) : affected.reason ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {affected.reason}
          </p>
        ) : null}
        {status === 'failed' && affected.revision?.error ? (
          <p className="mt-1 text-xs text-destructive">
            Rewrite failed: {affected.revision.error}
          </p>
        ) : null}
      </div>
      <div className="shrink-0">
        {status === 'pending' ? (
          <span className="text-xs text-muted-foreground">Refining…</span>
        ) : status === 'proposed' ? (
          <Button size="sm" variant="secondary" onClick={onReview}>
            Review
          </Button>
        ) : status === 'applied' ? (
          <Badge variant="secondary">Refined</Badge>
        ) : (
          <Button
            size="sm"
            onClick={onRefine}
            onClickCapture={(event) => {
              if (busy) {
                event.preventDefault();
                event.stopPropagation();
                notify.warning(
                  'Wait for the current action to finish, then try again.',
                );
              }
            }}
          >
            Refine
          </Button>
        )}
      </div>
    </div>
  );
}

/** Before/after review of a proposed rewrite with Apply / Dismiss. */
function ReviewDialog({
  runId,
  revision,
  onClose,
}: {
  runId: string;
  revision: CaseRevision;
  onClose: () => void;
}): JSX.Element {
  const apply = useApplyCaseRevision(runId);
  const dismiss = useDismissCaseRevision(runId);
  // The proposed rewrite is editable: seed a draft from it (HTML stripped to plain text) so the user
  // reads/edits clean text. An empty object keeps the form usable even if the proposal is somehow absent.
  const [draft, setDraft] = useState<CaseSpec>(() =>
    toPlainSpec(revision.proposed_spec ?? {}),
  );

  const onApply = (): void => {
    // Always send the draft: it is the proposal normalized to plain text (plus any edits), so applying
    // writes clean text back rather than the original HTML markup.
    apply.mutate(
      { revisionId: revision.id, editedSpec: draft },
      {
        onSuccess: (result) => {
          notify.success(
            result.tms_written
              ? 'Applied — the case was updated in the test management system'
              : 'Applied — the case will run with the refined text on the next re-run',
          );
          onClose();
        },
        onError: (error) => notify.error(error.message),
      },
    );
  };

  const onDismiss = (): void => {
    dismiss.mutate(revision.id, {
      onSuccess: onClose,
      onError: (error) => notify.error(error.message),
    });
  };

  const busy = apply.isPending || dismiss.isPending;
  // Guard the server contract: applying needs a proposal, a non-blank title, and at least one
  // runnable step (a non-blank structured step, or free-text steps). Mirrors the service's edit
  // gate so the button can't fire a request that only 400s.
  const canApply =
    revision.proposed_spec != null &&
    (draft.title ?? '').trim() !== '' &&
    ((draft.test_steps ?? []).some(
      (step) => (step.content ?? '').trim() !== '',
    ) ||
      (draft.test_steps_text ?? '').trim() !== '');

  return (
    <AppDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Review refined test case"
      description="Edit the proposed rewrite if needed, then apply it. The original is shown for comparison."
      className="max-w-3xl"
    >
      {/* Cap the comparison area and let it scroll, so a long case (many steps) can't push the
          footer buttons off-screen — the dialog itself has no max-height. */}
      <div className="grid max-h-[60vh] gap-4 overflow-y-auto pr-1 sm:grid-cols-2">
        <SpecView label="Original" spec={revision.original_spec} />
        <EditableSpecView
          label="Proposed rewrite (editable)"
          spec={draft}
          onChange={setDraft}
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="ghost"
          onClick={onDismiss}
          onClickCapture={(event) => {
            if (busy) {
              event.preventDefault();
              event.stopPropagation();
              notify.warning(
                'Wait for the current action to finish, then try again.',
              );
            }
          }}
        >
          Dismiss
        </Button>
        <Button
          onClick={onApply}
          onClickCapture={(event) => {
            if (busy || !canApply) {
              event.preventDefault();
              event.stopPropagation();
              notify.warning(
                'Add a title and at least one runnable step, and wait for the current action to finish.',
              );
            }
          }}
        >
          {apply.isPending ? 'Applying…' : 'Apply rewrite'}
        </Button>
      </div>
    </AppDialog>
  );
}

/**
 * The proposed-rewrite column as an editable form (MEDA-352): the user can edit the title,
 * precondition, each existing step's action + expected, and the expected result before applying —
 * no adding, removing, or reordering steps. Falls back to a single free-text steps field when the
 * spec carries `test_steps_text` instead of structured steps.
 */
function EditableSpecView({
  label,
  spec,
  onChange,
}: {
  label: string;
  spec: CaseSpec;
  onChange: (next: CaseSpec) => void;
}): JSX.Element {
  const steps = spec.test_steps ?? [];
  const setStep = (index: number, patch: Partial<TestStep>): void => {
    onChange({
      ...spec,
      test_steps: steps.map((step, current) =>
        current === index ? { ...step, ...patch } : step,
      ),
    });
  };
  return (
    <div className="space-y-2 rounded-md border border-border p-3 text-sm">
      <div className="text-xs font-semibold uppercase text-muted-foreground">
        {label}
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Title</Label>
        <Input
          value={spec.title ?? ''}
          onChange={(event) => onChange({ ...spec, title: event.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Precondition</Label>
        <Textarea
          rows={2}
          value={spec.precondition ?? ''}
          onChange={(event) =>
            onChange({ ...spec, precondition: event.target.value })
          }
        />
      </div>
      {steps.length > 0 ? (
        <div className="space-y-2">
          <Label className="text-xs">Steps</Label>
          {steps.map((step, index) => (
            <div
              key={index}
              className="space-y-1 rounded border border-border/60 p-2"
            >
              <div className="text-xs text-muted-foreground">
                Step {index + 1}
              </div>
              <Textarea
                rows={2}
                placeholder="Action"
                value={step.content ?? ''}
                onChange={(event) =>
                  setStep(index, { content: event.target.value })
                }
              />
              <Input
                placeholder="Expected"
                value={step.expected ?? ''}
                onChange={(event) =>
                  setStep(index, { expected: event.target.value })
                }
              />
            </div>
          ))}
        </div>
      ) : spec.test_steps_text != null ? (
        <div className="space-y-1">
          <Label className="text-xs">Steps</Label>
          <Textarea
            rows={4}
            value={spec.test_steps_text ?? ''}
            onChange={(event) =>
              onChange({ ...spec, test_steps_text: event.target.value })
            }
          />
        </div>
      ) : null}
      <div className="space-y-1">
        <Label className="text-xs">Expected result</Label>
        <Textarea
          rows={2}
          value={spec.expected_result ?? ''}
          onChange={(event) =>
            onChange({ ...spec, expected_result: event.target.value })
          }
        />
      </div>
    </div>
  );
}

/** Render one case spec (title, precondition, steps, expected) as a read-only column. */
function SpecView({
  label,
  spec,
}: {
  label: string;
  spec: CaseSpec | null;
}): JSX.Element {
  // Strip the stored HTML so the comparison reads as plain text, matching the editable column.
  const plain = spec ? toPlainSpec(spec) : null;
  const steps = plain?.test_steps ?? [];
  return (
    <div className="space-y-1 rounded-md border border-border p-3 text-sm">
      <div className="text-xs font-semibold uppercase text-muted-foreground">
        {label}
      </div>
      <div className="font-medium">{plain?.title || '—'}</div>
      {plain?.precondition ? (
        <div className="text-xs">
          <span className="font-medium">Precondition:</span>{' '}
          {plain.precondition}
        </div>
      ) : null}
      {steps.length > 0 ? (
        <ol className="list-decimal space-y-0.5 pl-4 text-xs">
          {steps.map((step, index) => (
            <li key={index}>
              {step.content}
              {step.expected ? (
                <span className="text-muted-foreground">
                  {' '}
                  → {step.expected}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : plain?.test_steps_text ? (
        // A case with free-text steps carries no structured test_steps — fall back to the text form
        // so the reviewer still sees the steps they are comparing before applying.
        <p className="whitespace-pre-line text-xs">{plain.test_steps_text}</p>
      ) : null}
      {plain?.expected_result ? (
        <div className="text-xs">
          <span className="font-medium">Expected:</span> {plain.expected_result}
        </div>
      ) : null}
    </div>
  );
}
